// src/components/merchant/MerchantLayout.js
import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiPackage,
  FiShoppingBag,
  FiLogOut,
  FiMenu,
  FiX,
  FiPlus,
  FiCreditCard,
  FiHome,
  FiSettings,
  FiShield,
  FiActivity,
  FiDollarSign,
  FiUsers,
  FiMessageCircle,
  FiLifeBuoy,
  FiCode,
  FiTag,
  FiBell,
  FiGift,
  FiExternalLink,
  FiAlertTriangle
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { notifAPI, merchantAPI } from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import { normalizeImagePath } from '../../utils/imageUtils';
import GlobalSearch from '../common/GlobalSearch';
import ProfileMenu from '../common/ProfileMenu';
import GlowButton from '../ui/GlowButton';
import SidebarProfileCard from '../common/SidebarProfileCard';
import EmailVerificationBanner from './EmailVerificationBanner';

const MerchantLayout = () => {
  const { settings } = useSettings();
  const brandLogo = settings?.logo ? normalizeImagePath(settings.logo) : null;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Densité d'affichage (zoom global, persisté). 0.8 = vue plus dense.
  const [merchantZoom, setMerchantZoom] = useState(() => {
    try { return localStorage.getItem('merchant_zoom') || '0.8'; } catch (e) { return '0.8'; }
  });
  const changeZoom = (v) => {
    setMerchantZoom(v);
    try { localStorage.setItem('merchant_zoom', v); } catch (e) {}
  };
  const [unreadCount, setUnreadCount] = useState(0);
  const [storeRef, setStoreRef] = useState(null); // slug/id boutique publique
  const [badges, setBadges] = useState({ orders: 0, messages: 0, leads: 0 });
  const [restricted, setRestricted] = useState(false); // 4.5 — publication suspendue

  useEffect(() => {
    let alive = true;
    const fetchUnread = async () => {
      try {
        const { data } = await notifAPI.unreadCount();
        if (alive) setUnreadCount(Number(data?.count) || 0);
      } catch (e) {}
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Recupere l'identifiant public de la boutique (pour le bouton "Ma boutique")
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await merchantAPI.getProfile();
        const m = (r && r.data && (r.data.data || r.data.merchant || r.data)) || {};
        const ref = m.slug || m.id || m._id;
        if (alive && ref) setStoreRef(ref);
        if (alive) setRestricted(!!m.disintermediationRestricted);
      } catch (e) { /* bouton masque si indisponible */ }
    })();
    return () => { alive = false; };
  }, []);

  const handleLogout = () => {
    try {
      if (typeof logout === 'function') logout();
    } catch (err) {}
    navigate('/login');
  };

  const menuSections = [
    {
      title: null,
      items: [
        { path: '/merchant/dashboard', icon: FiHome, label: 'Tableau de bord' }
      ]
    },
    {
      title: 'Ventes',
      items: [
        { path: '/merchant/deals', icon: FiPackage, label: 'Mes Deals' },
        { path: '/merchant/orders', icon: FiShoppingBag, label: 'Mes commandes' },
        { path: '/merchant/vouchers', icon: FiTag, label: 'Coupons' },
        { path: '/merchant/promos', icon: FiTag, label: 'Codes Promo' },
        { path: '/merchant/rewards', icon: FiGift, label: 'Récompenses' }
      ]
    },
    {
      title: 'Relation client',
      items: [
        { path: '/merchant/messages', icon: FiMessageCircle, label: 'Messages' }
      ]
    },
    {
      title: 'Finances',
      items: [
        { path: '/merchant/wallet', icon: FiDollarSign, label: 'Revenus' },
        { path: '/merchant/subscription', icon: FiCreditCard, label: 'Mon Abonnement' }
      ]
    },
    {
      title: 'Compte & outils',
      items: [
        { path: '/merchant/kyc', icon: FiShield, label: 'Vérification' },
        { path: '/merchant/team', icon: FiUsers, label: 'Équipe' },
        { path: '/merchant/notifications', icon: FiBell, label: 'Notifications' },
        { path: '/merchant/activity', icon: FiActivity, label: 'Historique' },
        { path: '/merchant/settings', icon: FiSettings, label: 'Paramètres' },
        { path: '/merchant/developer', icon: FiCode, label: 'API & Webhooks' },
        { path: '/merchant/tickets', icon: FiLifeBuoy, label: 'Support' }
      ]
    }
  ];

  const isActive = (path, exact = false) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  // Compteurs "à traiter" pour les badges sidebar (commandes/messages/leads).
  useEffect(() => {
    let alive = true;
    const fetchBadges = async () => {
      try {
        const { data } = await merchantAPI.getBadges();
        const d = data?.data || {};
        if (alive) setBadges({
          orders: Number(d.orders) || 0,
          messages: Number(d.messages) || 0,
          leads: Number(d.leads) || 0
        });
      } catch (e) {}
    };
    fetchBadges();
    const t = setInterval(fetchBadges, 60000);
    // Rafraîchissement immédiat à la demande (ex. quand une conversation est ouverte).
    const onRefresh = () => fetchBadges();
    window.addEventListener('badges:refresh', onRefresh);
    return () => { alive = false; clearInterval(t); window.removeEventListener('badges:refresh', onRefresh); };
  }, []);

  // Mappe un chemin de menu vers son compteur de badge.
  const badgeFor = (path) => {
    if (path === '/merchant/orders') return badges.orders;
    if (path === '/merchant/messages') return badges.messages;
    if (path === '/merchant/leads') return badges.leads;
    return 0;
  };
  const fmtBadge = (n) => (!n || n <= 0) ? null : (n > 9 ? '9+' : String(n));

  return (
    <div className="flex bg-gray-100" style={{ zoom: merchantZoom, height: `calc(100vh / ${merchantZoom})` }}>
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-6 border-b shrink-0">
            <Link to="/merchant/deals" className="flex items-center">
              {brandLogo ? (
                <img src={brandLogo} alt={settings?.siteName || 'Logo'} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.outerHTML = `<span class='text-2xl font-bold text-primary-600'>${settings?.siteName || 'MAXULA'}</span>`; }} className="h-9 w-auto object-contain" />
              ) : (
                <span className="text-2xl font-bold text-primary-600">
                  {settings?.siteName || 'MAXULA'}
                </span>
              )}
              <span className="ml-2 text-sm text-gray-600">Merchant</span>
            </Link>

            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-600 hover:text-gray-900"
              aria-label="Fermer le menu"
            >
              <FiX size={24} />
            </button>
          </div>

          <div className="px-6 py-4">
            <Link
              to="/merchant/deals/new"
              className="flex items-center justify-center w-full px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
              onClick={() => setSidebarOpen(false)}
            >
              <FiPlus className="mr-2" />
              Créer un Deal
            </Link>
          </div>

          <nav
            className="flex-1 px-4 py-4 space-y-1 overflow-y-auto"
            aria-label="Navigation marchand"
          >
            {menuSections.map((section, si) => (
              <div key={si} className={si > 0 ? 'pt-3' : ''}>
                {section.title && (
                  <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {section.title}
                  </p>
                )}
                {section.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                      isActive(item.path, item.exact)
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    aria-current={isActive(item.path, item.exact) ? 'page' : undefined}
                  >
                    <item.icon className="mr-3" size={20} aria-hidden="true" />
                    <span className="flex-1">{item.label}</span>
                    {fmtBadge(badgeFor(item.path)) && (
                      item.path === '/merchant/orders' ? (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSidebarOpen(false); navigate('/merchant/orders?filter=to_process'); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSidebarOpen(false); navigate('/merchant/orders?filter=to_process'); } }}
                          title="Voir les commandes à traiter"
                          className="ml-2 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full hover:bg-red-600 cursor-pointer"
                        >
                          {fmtBadge(badgeFor(item.path))}
                        </span>
                      ) : (
                        <span className="ml-2 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                          {fmtBadge(badgeFor(item.path))}
                        </span>
                      )
                    )}
                  </Link>
                ))}
              </div>
            ))}
          </nav>

          <div className="p-4 border-t shrink-0 space-y-2">
            <SidebarProfileCard
              user={user}
              scope="merchant"
              to="/merchant/profile"
              onNavigate={() => setSidebarOpen(false)}
            />
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <FiLogOut className="mr-3" size={20} />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm border-b h-16 flex items-center gap-4 px-4 sm:px-6">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-600 hover:text-gray-900 shrink-0"
            aria-label="Ouvrir le menu"
          >
            <FiMenu size={24} />
          </button>

          <div className="hidden md:block flex-1 max-w-3xl">
            <GlobalSearch scope="merchant" />
          </div>

          <div className="flex items-center gap-1 ml-auto">
            <select
              value={merchantZoom}
              onChange={(e) => changeZoom(e.target.value)}
              title="Densité d'affichage"
              className="hidden sm:block text-xs text-gray-600 border border-gray-200 rounded-md px-1.5 py-1 mr-1 focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer bg-white"
            >
              <option value="1">100%</option>
              <option value="0.9">90%</option>
              <option value="0.8">80%</option>
              <option value="0.75">75%</option>
            </select>
            {user?.role === 'admin' && (
              <button
                type="button"
                onClick={() => navigate('/admin/deals')}
                title="Quitter l'espace marchand et revenir au tableau de bord admin"
                className="inline-flex items-center gap-1.5 mr-1 rounded-lg bg-gray-900 text-white text-xs sm:text-sm font-semibold px-3 py-2 hover:bg-gray-800 transition-colors shrink-0"
              >
                <FiShield size={16} />
                <span className="hidden sm:inline">Retour à l'admin</span>
              </button>
            )}
            {storeRef && (
              <GlowButton
                variant="green"
                href={`/merchants/${storeRef}`}
                icon={FiExternalLink}
                size="sm"
                title="Voir ma boutique publique"
                className="mr-1"
              >
                <span className="hidden sm:inline">Ma boutique</span>
              </GlowButton>
            )}
            <Link to="/merchant/notifications" className="relative text-gray-600 hover:text-[#e11d7b] p-2" aria-label="Notifications">
              <FiBell size={22} />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 bg-[#e11d7b] text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
            <ProfileMenu user={user} scope="merchant" onLogout={handleLogout} />
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
          {/* Rappel de verification d'email : monte ICI (et non sur le seul
              tableau de bord) pour qu'il reste visible sur chaque page du
              back-office marchand tant que l'email n'est pas confirme. */}
          <EmailVerificationBanner />

          {restricted && (
            <div className="m-4 mb-0 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <FiAlertTriangle className="shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Publication suspendue</p>
                <p>
                  Suite à des tentatives répétées de partage de coordonnées en dehors de Maxula,
                  la publication de nouveaux deals est temporairement suspendue. Contactez le support
                  (contact@maxula.ma) pour régulariser votre situation.
                </p>
              </div>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MerchantLayout;
